import { findModelForEditing, submitServerChange } from 'components/dataCreationForms/common';
import {
  EditContext,
  OpenEditDialogParams,
} from 'components/dataCreationForms/types';
import { getLevelsStoreState, useLevels } from 'store/level';
import { levelClient } from 'shared/domain/level/levelClient';
import { levelOnViewToEditModel } from 'shared/domain/level/mapping/toModel';
import { levelModelToLevelOnView } from 'shared/domain/level/mapping/toView';
import { EditLevelOnView } from 'shared/domain/level/types/view';
import { useCancelConfirmation } from 'presentation/dialogForms/dialogFormsHooks';
import {
  ComponentType,
  ReactElement,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  handleFormValidation,
  validateLevelOnViewEdit,
} from '../validation';
import { useProject } from 'store/project';
import { useSites } from 'store/site';

const FORM_NAME = 'level';
const SUBMIT_EVENT_TYPE = 'submit-level';
const WithEditLevelContext = createContext<
  EditContext<EditLevelOnView> | undefined
>(undefined);

function WithEditLevel({
  children,
  onSuccessCallback,
}: {
  children: ReactElement;
  onSuccessCallback?: (_id: string, label: string) => void;
}): ReactElement {
  const {
    items: sites,
  } = useSites();
  const {
    items: levels,
  } = useLevels();
  const { organizationId, _id: projectId } = useProject();
  const [isPosting, setIsPosting] = useState<boolean>(false);
  const releaseSubmitEvent = useCallback(() => {
    window.dispatchEvent(new CustomEvent(SUBMIT_EVENT_TYPE));
  }, []);

  const [existingValues, setExistingValues] = useState<
    EditLevelOnView | undefined
  >(undefined);

  const [open, setOpen] = useState(false);

  const openDialog = useCallback(
    ({ _id }: OpenEditDialogParams) => {
      const existingLevelModel = findModelForEditing(levels, _id);
      if (existingLevelModel) {
        const levelOnView = levelModelToLevelOnView(
          existingLevelModel,
          organizationId
        );
        const currentSite = sites.find(
          (s) => s._id === levelOnView.siteId
        );
        setExistingValues({
          ...levelOnView,
          site: {
            _id: currentSite?._id || '',
            label: currentSite?.label || '',
          },
        });
        setOpen(true);
      }
    },
    [levels, organizationId, sites]
  );

  const onSuccess = useCallback(
    (_id, label) => onSuccessCallback?.(_id, label),
    [onSuccessCallback]
  );
  const confirmCancel = useCancelConfirmation(FORM_NAME);
  const openConfirmationDialog = useCallback(async () => {
    const { cancelConfirmed } = await confirmCancel();
    if (!cancelConfirmed) {
      return;
    }
    setOpen(false);
  }, [confirmCancel]);

  const submitForm = useCallback(
    async (values: EditLevelOnView) => {
      const levelEditModel = levelOnViewToEditModel(values, projectId);
      const { saved } = await submitServerChange({
        change: () => levelClient.edit(levelEditModel),
        setOpen,
        setIsPosting,
        action: 'updating a level',
      });
      if (saved) {
        void getLevelsStoreState().actions.reload();
      }
    },
    [projectId]
  );

  const ctx: EditContext<EditLevelOnView> = useMemo(() => {
    return {
      open,
      openDialog,
      closeDialog: openConfirmationDialog,
      submitForm,
      isPosting,
      existingValues,
      SUBMIT_EVENT_TYPE,
      releaseSubmitEvent,
      validate: validateLevelOnViewEdit,
      handleFormValidation,
      onSuccess,
      formName: FORM_NAME,
    };
  }, [
    openConfirmationDialog,
    existingValues,
    isPosting,
    open,
    openDialog,
    releaseSubmitEvent,
    submitForm,
    onSuccess,
  ]);

  return (
    <WithEditLevelContext.Provider value={ctx}>
      {children}
    </WithEditLevelContext.Provider>
  );
}

function useEditLevel(): EditContext<EditLevelOnView> {
  const context = useContext(WithEditLevelContext);
  if (context === undefined) {
    throw new Error(
      'useEditLevel must be used within an EditLevelContext'
    );
  }
  return context;
}

const withEditLevel =
  (Component: ComponentType<any>) =>
  ({ ...props }): ReactElement => (
    <WithEditLevel>
      <Component {...props} />
    </WithEditLevel>
  );

export { WithEditLevel, useEditLevel, withEditLevel };
